import { Button } from "@/components/ui/button";
import { MapPin, Video } from "lucide-react";
import { motion } from "framer-motion";
import { fadeInUp, fadeInLeft, fadeInRight, floatingBlob, buttonHover } from "@/lib/animations";

const HeroSection = () => {
  return <section id="hero" className="relative min-h-screen flex items-center justify-center px-4 py-16 pt-24 overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full z-0">
      {/* Desktop Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover hidden lg:block"
          // @ts-ignore
          webkit-playsinline=""
          x5-playsinline=""
          src="/videos/hero-background.mp4"
        />
        {/* Mobile Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover lg:hidden"
          // @ts-ignore
          webkit-playsinline=""
          x5-playsinline=""
          src="/videos/hero-background-mobile.mp4"
        />
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px]" />
      </div>
      
      {/* Animated organic blob shapes */}
      <motion.div className="absolute top-20 left-10 w-64 h-64 bg-primary/8 rounded-full blur-3xl z-[1]" variants={floatingBlob} animate="animate" />
      <motion.div className="absolute bottom-20 right-10 w-80 h-80 bg-primary/5 rounded-full blur-3xl z-[1]" variants={floatingBlob} animate="animate" style={{
      animationDelay: '2s'
    }} />
      <motion.div className="absolute top-1/2 left-1/4 w-40 h-40 bg-accent/30 rounded-full blur-2xl z-[1]" animate={{
      y: [0, 30, 0],
      x: [0, -20, 0],
      scale: [1, 1.1, 1]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: "easeInOut"
    }} />
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div className="text-center lg:text-left" initial="hidden" animate="visible" variants={{
          hidden: {
            opacity: 0
          },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.15
            }
          }
        }}>
            <motion.h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight" variants={fadeInUp}>
              O que você sente, mas não consegue{" "}
              <motion.span className="text-primary inline-block" initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.5,
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1]
            }}>
                colocar em palavras?
              </motion.span>
            </motion.h1>
            
            <motion.p className="text-lg sm:text-xl text-muted-foreground mb-10 leading-relaxed max-w-xl" variants={fadeInUp}>
              Às vezes a gente sabe que algo não está bem, mas não consegue explicar. 
              Vamos juntos dar nome aos seus desafios e construir uma vida com mais clareza e leveza.
            </motion.p>
            
            <motion.div className="mb-8" variants={fadeInUp}>
              <motion.div variants={buttonHover} initial="rest" whileHover="hover" whileTap="tap">
                <Button variant="hero" size="hero" className="rounded-full shadow-lg hover:shadow-xl transition-all duration-300" onClick={() => window.open('https://wa.me/5511999999999', '_blank')}>
                  Agendar uma consulta
                </Button>
              </motion.div>
            </motion.div>
            
            <motion.div className="flex flex-col sm:flex-row gap-4 text-sm text-muted-foreground justify-center lg:justify-start" variants={fadeInUp}>
              <motion.span className="flex items-center gap-2 bg-accent/50 px-4 py-2 rounded-full" whileHover={{
              scale: 1.05,
              backgroundColor: "hsl(var(--accent))"
            }} transition={{
              duration: 0.2
            }}>
                <MapPin className="w-4 h-4 text-primary" />
                Presencial em Esteio/RS
              </motion.span>
              <motion.span className="flex items-center gap-2 bg-accent/50 px-4 py-2 rounded-full" whileHover={{
              scale: 1.05,
              backgroundColor: "hsl(var(--accent))"
            }} transition={{
              duration: 0.2
            }}>
                <Video className="w-4 h-4 text-primary" />
                Online para todo Brasil
              </motion.span>
            </motion.div>
          </motion.div>
          
          <motion.div className="flex justify-center lg:justify-end" initial="hidden" animate="visible" variants={fadeInRight}>
            <div className="relative">
              {/* Animated organic background shape */}
              <motion.div className="absolute -inset-8 bg-gradient-to-br from-primary/20 via-accent/30 to-primary/10 rounded-[60%_40%_30%_70%/60%_30%_70%_40%] blur-xl" animate={{
              borderRadius: ["60% 40% 30% 70% / 60% 30% 70% 40%", "40% 60% 70% 30% / 30% 60% 40% 70%", "60% 40% 30% 70% / 60% 30% 70% 40%"]
            }} transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }} />
              
              <motion.div className="relative" initial={{
              opacity: 0,
              scale: 0.8,
              rotate: -5
            }} animate={{
              opacity: 1,
              scale: 1,
              rotate: 0
            }} transition={{
              duration: 0.8,
              delay: 0.3,
              ease: [0.22, 1, 0.36, 1]
            }}>
                <motion.div className="w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-[60%_40%_30%_70%/60%_30%_70%_40%] overflow-hidden shadow-2xl" whileHover={{
                scale: 1.02
              }} transition={{
                duration: 0.3
              }}>
                  {/* eslint-disable-next-line react/no-unknown-property */}
                  <img alt="Leonardo Maragno - Psicólogo" className="w-full h-full object-cover" src="/lovable-uploads/e07257d8-9698-43b0-a310-87e4ad3b675c.png" {...{ fetchpriority: "high" } as any} />
                </motion.div>
                
                {/* Identification card with slide animation */}
                <motion.div className="absolute -bottom-4 -left-4 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg p-4" initial={{
                opacity: 0,
                x: -30,
                y: 30
              }} animate={{
                opacity: 1,
                x: 0,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 0.8,
                ease: [0.22, 1, 0.36, 1]
              }} whileHover={{
                scale: 1.05,
                y: -5
              }}>
                  <h2 className="text-lg font-semibold text-foreground">Leonardo Maragno</h2>
                  <p className="text-sm text-primary font-medium">Psicólogo · CRP 07/43071 </p>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
export default HeroSection;